// client/src/components/map/AssetInfoCard.tsx
//
// What a click on an asset's marker shows (ST-00270), the same on Replay and Live View: its
// name, its status at that moment in the shared status colours (map/assetStatus key + label,
// with the motion in words), and its speed there. Rendered inside a react-map-gl <Popup> with
// `className={ASSET_INFO_POPUP_CLASS}` (themed in index.css). Callers add their own rows (last
// update, zone) and actions (Live View's Details) as children.

import type { ReactNode } from 'react';
import { ASSET_STATUS, ASSET_STATUS_CLASSES, type AssetStatusKey } from './assetStatus';

/** The Popup class that themes the card's surface and tip (src/index.css). */
export const ASSET_INFO_POPUP_CLASS = 'asset-info-popup';

export interface AssetInfoCardProps {
  /** Registration (or fleet number). */
  name: string;
  /** A second line: the asset's label or kind. */
  subtitle?: string | null;
  statusKey: AssetStatusKey;
  /** The status words (default: the key's label). */
  statusLabel?: string;
  /** Motion in words: "moving", "standing", "no position at this time". */
  motionText?: string | null;
  /** km/h at that point; null when unknown. */
  speedKmh: number | null | undefined;
  /** When ("at 14:32:10", "last report 09:58"). */
  when?: string | null;
  /** The `when` line is a warning (no recent position). */
  whenStale?: boolean;
  /** Id for aria-labelledby on the surrounding dialog. */
  titleId?: string;
  children?: ReactNode;
}

export default function AssetInfoCard({
  name,
  subtitle,
  statusKey,
  statusLabel,
  motionText,
  speedKmh,
  when,
  whenStale = false,
  titleId,
  children,
}: AssetInfoCardProps) {
  const label = statusLabel || ASSET_STATUS[statusKey].label;
  const speed =
    typeof speedKmh === 'number' && Number.isFinite(speedKmh)
      ? `${Math.round(speedKmh)} km/h`
      : '—';
  return (
    <div className="min-w-[200px] max-w-[260px] text-sm text-slate-700" data-asset-info={statusKey}>
      <p id={titleId} className="font-bold text-base text-slate-900 leading-tight pr-5">
        {name}
      </p>
      {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-center">
        <dt className="text-xs text-slate-500">Status</dt>
        <dd className="min-w-0">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${ASSET_STATUS_CLASSES[statusKey].pill}`}
            data-status-key={statusKey}
          >
            <span aria-hidden="true" className="leading-none">
              {ASSET_STATUS[statusKey].glyph}
            </span>
            {label}
          </span>
          {motionText && <span className="ml-1.5 text-xs text-slate-500">{motionText}</span>}
        </dd>
        <dt className="text-xs text-slate-500">Speed</dt>
        <dd className="font-semibold text-slate-900 tabular-nums">{speed}</dd>
      </dl>
      {when && (
        <p
          className={`mt-1.5 text-xs ${whenStale ? 'text-red-700 font-medium' : 'text-slate-500'}`}
        >
          {when}
        </p>
      )}
      {children}
    </div>
  );
}
